/**
 * The chrome around a how-to page, in five languages.
 *
 * `honestlyHeading` is the one that matters. It labels the block that answers
 * the question before any step, and it is phrased as a statement rather than a
 * disclaimer heading ("Read this first", not "Disclaimer") because a reader
 * skips anything that looks like small print — which would defeat the entire
 * point of putting it first.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

export interface HowtoUi {
  readonly honestlyHeading: string;
  readonly stepsHeading: string;
  readonly measureHeading: string;
  readonly faqHeading: string;
  readonly backToGuides: string;
  readonly indexTitle: string;
  readonly indexDescription: string;
  readonly indexH1: string;
  readonly indexStandfirst: string;
}

export const HOWTO_UI: Record<ContentLang, HowtoUi> = {
  en: {
    honestlyHeading: "Read this first",
    stepsHeading: "What actually works",
    measureHeading: "Measure it, so you know whether anything moved",
    faqHeading: "Questions",
    backToGuides: "All guides",
    indexTitle: "Face Improvement Guides — Honest How-Tos | Fazly",
    indexDescription:
      "Five guides to changing how your face looks, each starting with what is not possible: jawline, canthal tilt, hunter eyes, symmetry and mewing.",
    indexH1: "The guides",
    indexStandfirst:
      "Five questions with false premises attached, answered honestly before anything else.",
  },
  tr: {
    honestlyHeading: "Önce bunu oku",
    stepsHeading: "Gerçekten işe yarayan",
    measureHeading: "Ölç ki bir şeyin oynayıp oynamadığını bilesin",
    faqHeading: "Sorular",
    backToGuides: "Tüm rehberler",
    indexTitle: "Yüz İyileştirme Rehberleri — Dürüst Anlatımlar | Fazly",
    indexDescription:
      "Yüzünün görünüşünü değiştirmeye dair beş rehber; her biri neyin mümkün olmadığıyla başlıyor: çene hattı, canthal tilt, hunter eyes, simetri ve mewing.",
    indexH1: "Rehberler",
    indexStandfirst:
      "Yanlış varsayımlı beş soru, her şeyden önce dürüstçe cevaplanıyor.",
  },
  de: {
    honestlyHeading: "Lies das zuerst",
    stepsHeading: "Was tatsächlich wirkt",
    measureHeading: "Miss es, damit du weißt, ob sich etwas bewegt hat",
    faqHeading: "Fragen",
    backToGuides: "Alle Anleitungen",
    indexTitle: "Anleitungen fürs Gesicht — Ehrliche How-tos | Fazly",
    indexDescription:
      "Fünf Anleitungen zum Aussehen deines Gesichts, jede beginnt damit, was nicht möglich ist: Kieferlinie, Canthal Tilt, Hunter Eyes, Symmetrie und Mewing.",
    indexH1: "Die Anleitungen",
    indexStandfirst:
      "Fünf Fragen mit falscher Prämisse, ehrlich beantwortet, bevor irgendetwas anderes kommt.",
  },
  es: {
    honestlyHeading: "Lee esto primero",
    stepsHeading: "Lo que sí funciona",
    measureHeading: "Mídelo, para saber si algo se movió",
    faqHeading: "Preguntas",
    backToGuides: "Todas las guías",
    indexTitle: "Guías Faciales — Cómo Hacerlo, Honestamente | Fazly",
    indexDescription:
      "Cinco guías para cambiar cómo se ve tu cara, cada una empezando por lo que no es posible: mandíbula, canthal tilt, hunter eyes, simetría y mewing.",
    indexH1: "Las guías",
    indexStandfirst:
      "Cinco preguntas con premisas falsas, respondidas con honestidad antes que nada.",
  },
  pt: {
    honestlyHeading: "Leia isto primeiro",
    stepsHeading: "O que funciona de verdade",
    measureHeading: "Meça, para saber se alguma coisa mudou",
    faqHeading: "Perguntas",
    backToGuides: "Todos os guias",
    indexTitle: "Guias Faciais — Como Fazer, Honestamente | Fazly",
    indexDescription:
      "Cinco guias para mudar a aparência do seu rosto, cada um começando pelo que não é possível: mandíbula, canthal tilt, hunter eyes, simetria e mewing.",
    indexH1: "Os guias",
    indexStandfirst:
      "Cinco perguntas com premissas falsas, respondidas com honestidade antes de tudo.",
  },
};
